import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { getProducts } from "@/lib/api";
import { CATEGORIES, SPECIES_FILTERS } from "@/data/site";
import ProductCard from "@/components/ProductCard";
import QuoteCTA from "@/components/QuoteCTA";
import { Reveal, Kicker, MaskedLine } from "@/components/Reveal";

const DIVISION_TABS = [
  { key: "", label: "All" },
  { key: "timber", label: "Timber" },
  { key: "doors", label: "Doors" },
  { key: "furniture", label: "Furniture" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const division = params.get("division") || "";
  const category = params.get("category") || "";
  const species = params.get("species") || "";
  const q = params.get("q") || "";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(q);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (division) p.division = division;
    if (category) p.category = category;
    if (species) p.species = species;
    if (q) p.q = q;
    getProducts(p)
      .then(setProducts)
      .finally(() => setLoading(false));
  }, [division, category, species, q]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key === "division") next.delete("category");
    setParams(next);
  };

  const categories = useMemo(
    () => (division ? CATEGORIES[division] || [] : Object.values(CATEGORIES).flat()),
    [division]
  );

  return (
    <div data-testid="shop-page">
      <section className="px-5 pb-10 pt-32 md:px-10 md:pt-40">
        <div className="mx-auto max-w-[1500px]">
          <Kicker>The Full Range</Kicker>
          <h1 className="mt-4 font-display text-5xl font-light tracking-tight md:text-7xl">
            <MaskedLine delay={0.1}>Shop Mr Kwila</MaskedLine>
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-ink/60 md:text-base">
            Every product is priced on request. Add pieces to your quote list and submit one enquiry — pricing,
            lead times and freight come back together.
          </p>
        </div>
      </section>

      <section className="px-5 pb-24 md:px-10">
        <div className="mx-auto grid max-w-[1500px] gap-10 lg:grid-cols-12">
          <aside className="lg:col-span-3" data-testid="shop-filters">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                update("q", searchInput);
              }}
              className="mb-8 flex items-center gap-2 border-b border-ink/20 pb-3"
            >
              <Search size={16} className="text-sand" />
              <input
                data-testid="shop-search-input"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search the range…"
                className="w-full bg-transparent text-sm outline-none placeholder:text-sand/70"
              />
            </form>

            <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.22em] text-sand">Division</p>
            <div className="mb-8 flex flex-wrap gap-2" data-testid="shop-division-filters">
              {DIVISION_TABS.map((d) => (
                <button
                  key={d.key}
                  data-testid={`filter-division-${d.key || "all"}`}
                  onClick={() => update("division", d.key)}
                  className={`rounded-full px-5 py-2 text-xs font-bold uppercase tracking-[0.12em] transition-colors ${
                    division === d.key ? "bg-ink text-cream" : "border border-ink/20 hover:bg-stone"
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>

            <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.22em] text-sand">Category</p>
            <div className="mb-8 flex flex-col items-start gap-1.5" data-testid="shop-category-filters">
              <button
                data-testid="filter-category-all"
                onClick={() => update("category", "")}
                className={`text-sm transition-colors ${!category ? "font-bold text-kwila" : "text-ink/70 hover:text-ink"}`}
              >
                All categories
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  data-testid={`filter-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => update("category", category === c ? "" : c)}
                  className={`text-left text-sm transition-colors ${category === c ? "font-bold text-kwila" : "text-ink/70 hover:text-ink"}`}
                >
                  {c}
                </button>
              ))}
            </div>

            <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.22em] text-sand">Timber Species</p>
            <div className="flex flex-col items-start gap-1.5" data-testid="shop-species-filters">
              <button
                data-testid="filter-species-all"
                onClick={() => update("species", "")}
                className={`text-sm transition-colors ${!species ? "font-bold text-kwila" : "text-ink/70 hover:text-ink"}`}
              >
                All species
              </button>
              {SPECIES_FILTERS.map((s) => (
                <button
                  key={s}
                  data-testid={`filter-species-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => update("species", species === s ? "" : s)}
                  className={`text-left text-sm transition-colors ${species === s ? "font-bold text-kwila" : "text-ink/70 hover:text-ink"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </aside>

          <div className="lg:col-span-9">
            <p className="mb-6 text-xs uppercase tracking-[0.18em] text-sand" data-testid="shop-result-count">
              {loading ? "Loading…" : `${products.length} product${products.length === 1 ? "" : "s"}`}
            </p>
            {!loading && products.length === 0 ? (
              <div className="rounded-sm border border-ink/15 p-12 text-center" data-testid="shop-empty-state">
                <p className="font-display text-3xl">Nothing here yet.</p>
                <p className="mx-auto mt-3 max-w-md text-sm text-ink/60">
                  This category is being stocked. Send a quote request with what you need — chances are our mill
                  can supply it.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-3" data-testid="shop-product-grid">
                {products.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <QuoteCTA />
    </div>
  );
}
